import type { SequenceStep } from '~/components/Sequence/sequenceSteps';
import { canMergeIntoGroup, canReceiveMerge } from '~/components/Sequence/sequenceSteps';
import type { DropTarget, LineAddr, StepAddr } from '~/components/Sequence/sequenceMove';
import { canDrop, parseLine, sameLine, serializeLine, stepAt } from '~/components/Sequence/sequenceMove';

/**
 * Whether a pointer-down should BEGIN a step drag, and the housekeeping that must happen when it does.
 *
 * Called by every surface that makes a step draggable (the spine node, a branch line's slots, a group
 * chip) — the element that owns the pointerdown owns this decision, rather than each surface trusting
 * whatever handler it was passed to re-derive it.
 *
 *  - An interactive child (the remove ✕, a wait input, a link) keeps its own gesture: return false, and
 *    crucially do NOT swallow the event — the button still needs its click.
 *  - Otherwise claim it: stop propagation so a nested step never also drags its container, and suppress
 *    the default. That last part is what stops a text selection: the browser fires a compat `mousedown`
 *    after `pointerdown`, and THAT is what begins a selection — without this, dragging a node sweeps a
 *    blue highlight across every label the cursor passes.
 */
export function beginStepGesture(e: React.PointerEvent): boolean {
  if (e.target instanceof Element && e.target.closest('button, input, textarea, select, a')) return false;
  e.stopPropagation();
  e.preventDefault();
  return true;
}

/**
 * The drag HIT-TEST for the sequence graph: given where every line is on screen and where the cursor is,
 * which drop does this gesture mean? Split out of the editor's pointer-move handler so the geometry is
 * testable — against real rects measured off a real rendered surface ({@link readLineRects}) as well as
 * synthetic ones — instead of only being exercised by hand.
 *
 * The rules, in order:
 *  1. Hovering a step that can receive the dragged step → MERGE onto it (form / join a parallel group).
 *     Deepest match wins, so a chip inside a group targets the chip, not the box around it.
 *  2. Otherwise pick the LINE the cursor is over — a line it's genuinely inside beats a merely nearby one,
 *     and the deepest such line wins (a nested arm beats the branch it hangs off).
 *  3. Slot the gap along the axis that line is actually laid out on (a group's chip row runs across).
 *  4. Reject anything {@link canDrop} refuses, so an invalid drop is never previewed or applied.
 * A gesture over empty space resolves to NOTHING and cancels on release, rather than snapping to a far line.
 */

/** The subset of `DOMRect` the hit-test needs (a real DOMRect satisfies it). */
export interface Rect {
  left: number;
  top: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
}

/** One line's measured geometry: the rects of its steps (which slot a reorder) + every rect anchoring it. */
export interface LineRects {
  line: LineAddr;
  /** This line's own steps, each with its model index. Empty for a line that has no steps yet. */
  steps: { index: number; rect: Rect; el: HTMLElement | null }[];
  /** Every rect tagged with this line — its steps plus, for an empty line, its `data-empty` anchor. */
  bounds: Rect[];
}

/** Where to paint the insertion bar: a horizontal rule across a vertical line, or vice versa. */
export interface BarRect {
  left: number;
  top: number;
  /** Set for a horizontal rule (a vertical line's gap); null → the bar is a vertical hairline. */
  width: number | null;
  /** Set for a vertical rule (a horizontal line's gap); null → the bar is a horizontal hairline. */
  height: number | null;
}

export interface DropResolution {
  drop: DropTarget;
  /** Insertion-bar placement for a reorder; null for a merge. */
  bar: BarRect | null;
  /** The element to outline for a merge; null for a reorder. */
  mergeEl: HTMLElement | null;
}

/** How far outside a line the cursor may sit and still target it. */
const NEAR = 44;

/**
 * Measure every line on the page. Each step carries `data-line` (its line's address) + `data-idx` (its
 * index in that line); a line with no steps yet carries a `data-empty` anchor instead, so it is reachable
 * too — without one you could never drag the FIRST step into a fresh spawn branch or switch route.
 */
export function readLineRects(root: ParentNode): LineRects[] {
  const byLine = new Map<string, LineRects>();
  for (const el of root.querySelectorAll<HTMLElement>('[data-line]')) {
    const line = parseLine(el.getAttribute('data-line'));
    if (line == null) continue;
    const key = serializeLine(line);
    const g = byLine.get(key) ?? { line, steps: [], bounds: [] };
    const rect = el.getBoundingClientRect();
    g.bounds.push(rect);
    // `Number(null)` is 0, so a `data-line` element with NO `data-idx` would silently register as step 0
    // of that line and steal drops aimed at the real first step. Require the attribute to be present.
    const rawIndex = el.getAttribute('data-idx');
    const index = rawIndex == null ? Number.NaN : Number(rawIndex);
    if (el.getAttribute('data-empty') == null && Number.isFinite(index)) g.steps.push({ index, rect, el });
    byLine.set(key, g);
  }
  return [...byLine.values()];
}

const contains = (r: Rect, x: number, y: number) => x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;

/** Merge target: the DEEPEST step under the cursor that can receive the dragged step. */
function resolveMerge(
  lines: LineRects[],
  pointer: { x: number; y: number },
  source: StepAddr,
  steps: SequenceStep[],
): DropResolution | null {
  if (!canMergeIntoGroup(stepAt(steps, source))) return null;
  let best: { addr: StepAddr; el: HTMLElement | null } | null = null;
  let bestDepth = -1;
  for (const g of lines) {
    for (const t of g.steps) {
      if (sameLine(g.line, source.line) && t.index === source.index) continue; // onto itself
      if (!contains(t.rect, pointer.x, pointer.y) || g.line.path.length <= bestDepth) continue;
      bestDepth = g.line.path.length;
      best = { addr: { line: g.line, index: t.index }, el: t.el };
    }
  }
  if (best == null || !canReceiveMerge(stepAt(steps, best.addr))) return null;
  return { drop: { kind: 'merge', onto: best.addr }, bar: null, mergeEl: best.el };
}

/**
 * Every line the cursor is plausibly over, BEST FIRST: a line it is genuinely inside beats a merely nearby
 * one, a deeper line beats the shallower one it nests in, and ties break on horizontal distance.
 *
 * Returns the whole ranked list, not just the winner, because the best line is not always a LEGAL target —
 * most obviously when you drag a container: your cursor sits right over the container's own arm, which is
 * the one line it can never be dropped into. Resolving to the winner alone made that gesture resolve to
 * NOTHING, so dragging a spawn anywhere near its own branch simply did nothing. The caller walks the list.
 */
function rankLines(lines: LineRects[], pointer: { x: number; y: number }): LineRects[] {
  const scored: { g: LineRects; score: number; dist: number }[] = [];
  for (const g of lines) {
    if (g.bounds.length === 0) continue;
    const left = Math.min(...g.bounds.map((r) => r.left));
    const right = Math.max(...g.bounds.map((r) => r.right));
    const top = Math.min(...g.bounds.map((r) => r.top));
    const bottom = Math.max(...g.bounds.map((r) => r.bottom));
    const inside = pointer.x >= left && pointer.x <= right && pointer.y >= top && pointer.y <= bottom;
    if (!inside && (pointer.x < left - NEAR || pointer.x > right + NEAR || pointer.y < top - NEAR || pointer.y > bottom + NEAR)) continue;
    scored.push({
      g,
      score: inside ? 1000 + g.line.path.length : 0,
      dist: inside ? 0 : Math.min(Math.abs(pointer.x - left), Math.abs(pointer.x - right)),
    });
  }
  return scored.sort((a, b) => b.score - a.score || a.dist - b.dist).map((s) => s.g);
}

/** Reorder target: which gap of `g` the cursor sits in, plus where to draw the insertion bar. */
function resolveReorder(g: LineRects, pointer: { x: number; y: number }): DropResolution | null {
  // Lines run vertically (spine, branch, spawn arm) EXCEPT a parallel group's chip row, which runs across.
  // Take that from the line's ADDRESS, not from measured spread: spread is 0 for a line with fewer than
  // two steps, so a single-chip group would be slotted vertically and its insertion side would flip on
  // sub-pixel cursor noise.
  const horizontal = g.line.path[g.line.path.length - 1]?.into === 'group';
  const sorted = g.steps.slice().sort((a, b) => (horizontal ? a.rect.left - b.rect.left : a.rect.top - b.rect.top));
  const along = horizontal ? pointer.x : pointer.y;
  const mid = (r: Rect) => (horizontal ? r.left + r.width / 2 : r.top + r.height / 2);
  let gap = 0;
  for (const s of sorted) if (along > mid(s.rect)) gap++;

  const first = sorted[0]?.rect ?? g.bounds[0];
  if (first == null) return null;
  const last = sorted[sorted.length - 1]?.rect ?? first;
  const start = (r: Rect) => (horizontal ? r.left : r.top);
  const end = (r: Rect) => (horizontal ? r.right : r.bottom);
  const at =
    sorted.length === 0
      ? start(first) + (horizontal ? first.width : first.height) / 2
      : gap === 0
        ? start(first) - 5
        : gap >= sorted.length
          ? end(last) + 5
          : (end(sorted[gap - 1]?.rect ?? last) + start(sorted[gap]?.rect ?? last)) / 2;
  const bar: BarRect = horizontal
    ? { left: at, top: Math.min(first.top, last.top), height: Math.max(first.height, last.height), width: null }
    : { top: at, left: first.left, width: Math.max(first.width, ...sorted.map((s) => s.rect.width)), height: null };
  return { drop: { kind: 'reorder', line: g.line, gap }, bar, mergeEl: null };
}

/**
 * Resolve a drag gesture into the drop it means, or null when the cursor isn't over anything droppable
 * (or the drop it would mean isn't allowed — see {@link canDrop}).
 */
export function resolveDrop(
  lines: LineRects[],
  pointer: { x: number; y: number },
  source: StepAddr,
  steps: SequenceStep[],
): DropResolution | null {
  // A merge the rules reject FALLS THROUGH to a reorder rather than dead-ending — hovering a chip inside a
  // parallel group is the everyday case (a group can't nest a group, but the chip row reorders fine), and
  // "the cursor is over something, yet nothing happens" is precisely how a drag reads as broken.
  const merge = resolveMerge(lines, pointer, source, steps);
  if (merge != null && canDrop(steps, source, merge.drop)) return merge;
  // Walk the ranked lines and take the first LEGAL one. Stopping at the best-ranked line meant a single
  // illegal candidate (a container's own arm, a switch branch that can't hold what you're dragging) killed
  // the whole gesture, instead of falling through to the line behind it.
  for (const g of rankLines(lines, pointer)) {
    const reorder = resolveReorder(g, pointer);
    if (reorder == null) continue;
    if (canDrop(steps, source, reorder.drop)) return reorder;
    // Releasing a step back on its OWN slot is a deliberate cancel, not an illegal target — fall through
    // there and the step would be flung into whichever neighbouring line happens to rank next. Only an
    // illegal target (a container's own arm, a line that can't hold this step) should try the next line.
    if (reorder.drop.kind === 'reorder' && sameLine(reorder.drop.line, source.line)) return null;
  }
  return null;
}
